import React, { type FunctionComponent } from 'react'
import moment from 'moment'
import {
  type DateRangeOption,
  DateRangePicker,
  type DateRangePickerProps,
} from 'gin-and-tonic/common/date-range-picker/DateRangePicker'

interface ProductsDateRangePickerProps
  extends Omit<DateRangePickerProps, 'customStartDate' | 'customEndDate' | 'onDateRangeChanged'> {
  customStartDate?: string
  customEndDate?: string
  onDateRangeChanged: (o: DateRangeOption, customStartDate?: string, customEndDate?: string) => void
}

const dateFormat = 'YYYY-MM-DD'

/**
 * Checks if the provided string matches the expected dateFormat.
 *
 * @param dateString the string date to test
 * @returns a bool
 */
const isValidDateFormat = dateString => moment(dateString, dateFormat, true).isValid()

/**
 * Safely converts a string into a Moment, if it matches the expected dateFormat, otherwise returns undefined.
 *
 * @param dateString the string date to attempt parsing
 * @returns a Moment or undefined
 */
const stringToMoment = dateString =>
  isValidDateFormat(dateString) ? moment(dateString) : undefined

export const ProductsDateRangePicker: FunctionComponent<ProductsDateRangePickerProps> = ({
  customStartDate,
  customEndDate,
  onDateRangeChanged,
  ...rest
}) => (
  <DateRangePicker
    customStartDate={stringToMoment(customStartDate)}
    customEndDate={stringToMoment(customEndDate)}
    onDateRangeChanged={(newRange, customStart, customEnd) => {
      const stringStart = customStart?.format(dateFormat)
      const stringEnd = customEnd?.format(dateFormat)

      onDateRangeChanged(newRange, stringStart, stringEnd)
    }}
    {...rest}
  />
)
